/**
 * 栄養ページの下に固定するワンタップ記録バー（ページで食事を記録する入口）
 *
 * チップや候補をタップするとシートを開かずにすぐ記録し、toast の「元に戻す」で取り消せる。
 * 候補とチップは入力欄の上に開く。
 */

import { useState } from 'react'
import { toast } from 'sonner'

import type { FoodTemplate, MealLog, MealType } from '@/api/types/nutrition'
import { useSubmitLock } from '@/hooks/useSubmitLock'

import { type PalettePick, pickToItems } from './cart-utils'
import { FoodPalette } from './food-palette/food-palette'
import type { SheetInit } from './food-palette/palette-utils'
import { PAGE_LIST_MAX_HEIGHT } from './list-heights'
import { MealTypeSelect } from './meal-type-select'
import { MEAL_TYPE_LABELS } from './meal-types'
import { guessQuickAddMealType, quickAddItems } from './quick-add'
import { currentTime, formatMonthDay, inputToTime } from './utils'

type Props = {
  date: string
  isToday: boolean
  meals: MealLog[]
  templates: FoodTemplate[]
  /** 記録・取り消しのあとに画面を読み直す */
  onRecorded: () => Promise<void>
  /** 食事シートを開く（AI で詳しく追加・DB で検索・✨/⭐/🕘/🗄 のタブ） */
  onOpenSheet: (init: SheetInit) => void
}

function pickName(pick: PalettePick): string {
  return pick.kind === 'template' ? pick.template.name : pick.entry.name
}

const NEVER_SELECTED = () => false

export function QuickAddBar({ date, isToday, meals, templates, onRecorded, onOpenSheet }: Props) {
  const [mealType, setMealType] = useState<MealType>(() => guessQuickAddMealType(meals, isToday))
  const { isSubmitting, run } = useSubmitLock()
  // 過去の日を開いているときは、どの日に記録されるかを見えるところに出す
  const dayPrefix = isToday ? '' : `${formatMonthDay(date)} の`

  const handleUndo = async (undo: () => Promise<void>) => {
    try {
      await undo()
      await onRecorded()
      toast.success('取り消しました')
    } catch {
      toast.error('取り消しに失敗しました')
    }
  }

  const handlePick = (pick: PalettePick) =>
    run(async () => {
      try {
        const { undo } = await quickAddItems({
          date,
          mealType,
          eatenAt: isToday ? inputToTime(currentTime()) : null,
          items: pickToItems(pick),
          meals,
        })
        await onRecorded()
        toast.success(`${dayPrefix}${MEAL_TYPE_LABELS[mealType]}に「${pickName(pick)}」を追加しました`, {
          action: { label: '元に戻す', onClick: () => void handleUndo(undo) },
        })
      } catch {
        toast.error('記録に失敗しました')
      }
    })

  return (
    <FoodPalette
      templates={templates}
      isSelected={NEVER_SELECTED}
      onPick={(pick) => void handlePick(pick)}
      onAiText={(memo) => onOpenSheet({ tab: 'ai', memo })}
      onSearchDb={(dbQuery) => onOpenSheet({ tab: 'db', dbQuery })}
      onOpenList={(tab) => onOpenSheet({ tab })}
      aiLabel={(text) => `「${text}」を AI で詳しく追加`}
      placeholder={isToday ? '何を食べた？' : `${formatMonthDay(date)} に記録する食事を検索`}
      chipsWhenEmpty
      listClassName={PAGE_LIST_MAX_HEIGHT}
      disabled={isSubmitting}
      dropUp
      className="rounded-none border-0"
      leading={
        <MealTypeSelect
          value={mealType}
          onValueChange={setMealType}
          triggerClassName="h-8 w-auto shrink-0 gap-1 border-0 bg-transparent px-2 text-xs shadow-none"
          itemClassName="text-xs"
          aria-label="追加先の食事"
        />
      }
    />
  )
}
